import { useEffect, useRef } from 'react';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import XYZ from 'ol/source/XYZ';
import { fromLonLat } from 'ol/proj';
import { defaults as defaultControls } from 'ol/control';
import { defaults as defaultInteractions } from 'ol/interaction';
import 'ol/ol.css';

/** Köln (Cologne), Germany — default map center for Bazaarak. */
export const KOLN_CENTER = { lon: 6.9603, lat: 50.9375 } as const;

/** Local tiles from API wwwroot/OSM. */
export const LOCAL_OSM_TILE_URL = '/OSM/{z}/{x}/{y}.png';

/** Online OSM fallback when a local tile is missing. */
export const ONLINE_OSM_TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';

/** Zoom range matching the server tile mirror (see download-koln-osm-tiles.sh). */
export const LOCAL_OSM_MIN_ZOOM = 8;
export const LOCAL_OSM_MAX_ZOOM = 17;

/**
 * XYZ layer that prefers local `/OSM/{z}/{x}/{y}.png` and falls back to
 * tile.openstreetmap.org when the local file is missing / errors.
 */
export function createBazaarakTileLayer(): TileLayer<XYZ> {
  return new TileLayer({
    source: new XYZ({
      url: LOCAL_OSM_TILE_URL,
      crossOrigin: 'anonymous',
      minZoom: LOCAL_OSM_MIN_ZOOM,
      maxZoom: LOCAL_OSM_MAX_ZOOM,
      attributions: [],
      tileLoadFunction: (tile, src) => {
        const imageTile = tile as import('ol/ImageTile').default;
        const img = imageTile.getImage() as HTMLImageElement;
        img.crossOrigin = 'anonymous';
        img.onerror = () => {
          const m = src.match(/\/OSM\/(\d+)\/(\d+)\/(\d+)\.png/);
          if (!m) return;
          img.onerror = null;
          img.src = `https://tile.openstreetmap.org/${m[1]}/${m[2]}/${m[3]}.png`;
        };
        img.src = src;
      },
    }),
  });
}

export function createMapTileLayers(): TileLayer[] {
  return [createBazaarakTileLayer()];
}

interface BazaarakMapProps {
  className?: string;
  centerLon?: number;
  centerLat?: number;
  zoom?: number;
  /** Show +/- zoom buttons (default true). */
  showControls?: boolean;
  /** Allow pan/zoom (default true). Set false for static previews. */
  interactive?: boolean;
  onReady?: (map: Map) => void;
}

/** Shared OpenLayers map shell — local OSM tiles with online fallback. */
export default function BazaarakMap({
  className = 'w-full h-full',
  centerLon = KOLN_CENTER.lon,
  centerLat = KOLN_CENTER.lat,
  zoom = 12,
  showControls = true,
  interactive = true,
  onReady,
}: BazaarakMapProps) {
  const ref = useRef<HTMLDivElement>(null);
  const mapRef = useRef<Map | null>(null);
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  useEffect(() => {
    if (!ref.current) return;

    const clampedZoom = Math.min(LOCAL_OSM_MAX_ZOOM, Math.max(LOCAL_OSM_MIN_ZOOM, zoom));
    const map = new Map({
      target: ref.current,
      layers: createMapTileLayers(),
      view: new View({
        center: fromLonLat([centerLon, centerLat]),
        zoom: clampedZoom,
        minZoom: LOCAL_OSM_MIN_ZOOM,
        maxZoom: LOCAL_OSM_MAX_ZOOM,
      }),
      controls: defaultControls({
        zoom: showControls,
        rotate: false,
        attribution: false,
      }),
      interactions: defaultInteractions({
        doubleClickZoom: interactive,
        dragPan: interactive,
        mouseWheelZoom: interactive,
        pinchZoom: interactive,
        shiftDragZoom: interactive,
        pinchRotate: false,
        altShiftDragRotate: false,
        keyboard: interactive,
      }),
    });
    mapRef.current = map;
    onReadyRef.current?.(map);

    // Modal / animated containers often need a delayed size sync.
    const t1 = window.setTimeout(() => map.updateSize(), 50);
    const t2 = window.setTimeout(() => map.updateSize(), 300);

    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      map.setTarget(undefined);
      mapRef.current = null;
    };
    // Recreate only when interaction/control mode changes; center updates below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showControls, interactive]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const view = map.getView();
    const clampedZoom = Math.min(LOCAL_OSM_MAX_ZOOM, Math.max(LOCAL_OSM_MIN_ZOOM, zoom));
    view.setCenter(fromLonLat([centerLon, centerLat]));
    view.setZoom(clampedZoom);
    map.updateSize();
  }, [centerLon, centerLat, zoom]);

  return (
    <div
      ref={ref}
      className={className}
      style={{ touchAction: interactive ? undefined : 'none' }}
    />
  );
}
